import { Board } from './Board';
import { Piece } from './Piece';
import { ImageLoader } from '../utils/ImageLoader';

export class JigsawGame {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private board: Board | null = null;
  private image: HTMLImageElement | null = null;
  private selectedPiece: Piece | null = null;
  private selectedGroup: Set<Piece> = new Set();
  private groupOffsets: Map<Piece, { x: number; y: number }> = new Map();
  private rows: number = 4;
  private cols: number = 4;
  private dragStartX: number = 0;
  private dragStartY: number = 0;
  private originalGridRow: number = 0;
  private originalGridCol: number = 0;
  private isDragging: boolean = false;
  private currentMouseX: number = 0;
  private currentMouseY: number = 0;
  private isAnimatingWin: boolean = false;
  private animationStartTime: number = 0;
  private confetti: Array<{
    x: number;
    y: number;
    vx: number;
    vy: number;
    color: string;
    size: number;
    rotation: number;
    rotationSpeed: number;
  }> = [];
  private onCompleteCallback: (() => void) | null = null;
  private currentImageUrl: string = '';

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Could not get canvas context');
    }
    this.ctx = context;

    this.setupCanvas();
    this.setupEventListeners();
  }

  async initialize(imageUrl?: string, shuffle: boolean = true): Promise<void> {
    if (imageUrl) {
      // Let errors propagate so caller can handle them (e.g., delete puzzle from collection)
      await this.loadImageFromUrl(imageUrl);
      // Don't load a new image since we just loaded one
      await this.startNewGame(false, undefined, shuffle);
    } else {
      await this.loadDefaultImage();
      await this.startNewGame(true, undefined, shuffle);
    }
  }

  async loadCustomImage(dataUrl: string): Promise<void> {
    try {
      this.currentImageUrl = dataUrl;
      this.image = await ImageLoader.loadImage(dataUrl);
      // Start new game without loading a new image
      await this.startNewGame(false);
    } catch (error) {
      console.error('Failed to load custom image', error);
      // Fall back to default image
      await this.loadDefaultImage();
      await this.startNewGame(false);
    }
  }

  private setupCanvas(): void {
    const container = this.canvas.parentElement;
    if (container) {
      this.resizeCanvas(container.clientWidth, container.clientHeight);
    }

    let resizeTimeout: number | null = null;
    window.addEventListener('resize', () => {
      if (container) {
        // Debounce resize to avoid too many calculations
        if (resizeTimeout) {
          clearTimeout(resizeTimeout);
        }

        resizeTimeout = window.setTimeout(() => {
          this.handleResize();
          resizeTimeout = null;
        }, 100);
      }
    });
  }

  private resizeCanvas(width: number, height: number): void {
    const dpr = window.devicePixelRatio || 1;

    // Set the display size (CSS pixels)
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;

    // Set the backing store size (actual pixels)
    this.canvas.width = width * dpr;
    this.canvas.height = height * dpr;

    // Scale all drawing operations by the dpr
    this.ctx.scale(dpr, dpr);
  }

  handleResize(): void {
    const container = this.canvas.parentElement;
    if (!container) return;

    const newWidth = container.clientWidth;
    const newHeight = container.clientHeight;

    // Skip resize if container has no dimensions (hidden)
    if (newWidth === 0 || newHeight === 0) {
      return;
    }

    // Resize canvas with proper DPI scaling
    this.resizeCanvas(newWidth, newHeight);

    // If we have a board and image, recalculate layout
    if (this.board && this.image) {
      const maxBoardWidth = newWidth * 0.85;
      const maxBoardHeight = newHeight * 0.85;

      const aspectRatio = this.image.width / this.image.height;
      let boardWidth, boardHeight;

      if (maxBoardWidth / aspectRatio <= maxBoardHeight) {
        boardWidth = maxBoardWidth;
        boardHeight = maxBoardWidth / aspectRatio;
      } else {
        boardHeight = maxBoardHeight;
        boardWidth = maxBoardHeight * aspectRatio;
      }

      const offsetX = (newWidth - boardWidth) / 2;
      const offsetY = (newHeight - boardHeight) / 2;

      // Resize the board with new dimensions
      this.board.resize(boardWidth, boardHeight, offsetX, offsetY);

      // Recreate confetti if animating (to fit new canvas size)
      if (this.isAnimatingWin) {
        this.createConfetti();
      }
    }

    this.render();
  }

  async loadDefaultImage(): Promise<void> {
    // This should never be called - always pass an imageUrl
    console.error('loadDefaultImage called without imageUrl parameter - this should not happen');
    throw new Error('loadDefaultImage called without imageUrl');
  }

  async loadImageFromUrl(url: string): Promise<void> {
    try {
      this.currentImageUrl = url;
      this.image = await ImageLoader.loadImage(url);
    } catch (error) {
      console.error('Failed to load image from URL:', error);
      throw error; // Re-throw to let caller handle it
    }
  }

  private getPhotoCredit(): string {
    if (!this.currentImageUrl) return '';

    // Check URL for photo sources
    if (this.currentImageUrl.includes('pixabay.com')) {
      return 'Credit: Pixabay';
    } else if (this.currentImageUrl.includes('pexels.com')) {
      return 'Credit: Pexels';
    } else if (this.currentImageUrl.includes('picsum.photos')) {
      return 'Credit: Picsum Photos';
    }

    return '';
  }

  setGridSize(size: number): void {
    this.rows = size;
    this.cols = size;
  }

  getCurrentImageDataUrl(): string | null {
    if (!this.image) return null;

    // Create a temporary canvas to convert the image to a data URL
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = this.image.width;
    tempCanvas.height = this.image.height;
    const tempCtx = tempCanvas.getContext('2d');

    if (!tempCtx) return null;

    tempCtx.drawImage(this.image, 0, 0);
    return tempCanvas.toDataURL('image/jpeg', 0.8);
  }

  setOnCompleteCallback(callback: () => void): void {
    this.onCompleteCallback = callback;
  }

  async startNewGame(loadNewImage: boolean = true, imageUrl?: string, shuffle: boolean = true): Promise<void> {
    // Reset animation state
    this.isAnimatingWin = false;
    this.confetti = [];

    // Load a new random image (unless we're using a custom image)
    if (loadNewImage) {
      if (imageUrl) {
        await this.loadImageFromUrl(imageUrl);
      } else {
        await this.loadDefaultImage();
      }
    }

    if (!this.image) return;

    const container = this.canvas.parentElement;
    const displayWidth = container ? container.clientWidth : this.canvas.width;
    const displayHeight = container ? container.clientHeight : this.canvas.height;

    const maxBoardWidth = displayWidth * 0.85;
    const maxBoardHeight = displayHeight * 0.85;

    const aspectRatio = this.image.width / this.image.height;
    let boardWidth, boardHeight;

    if (maxBoardWidth / aspectRatio <= maxBoardHeight) {
      boardWidth = maxBoardWidth;
      boardHeight = maxBoardWidth / aspectRatio;
    } else {
      boardHeight = maxBoardHeight;
      boardWidth = maxBoardHeight * aspectRatio;
    }

    const offsetX = (displayWidth - boardWidth) / 2;
    const offsetY = (displayHeight - boardHeight) / 2;

    this.board = new Board(this.rows, this.cols, boardWidth, boardHeight, offsetX, offsetY);
    this.board.initialize();

    // Only shuffle if requested (not for completed puzzles)
    if (shuffle) {
      this.shufflePieces();
    } else {
      // If not shuffling, just render the solved puzzle
      this.render();
    }
  }

  shufflePieces(): void {
    if (!this.board) return;
    this.board.shuffle();
    this.render();
  }

  private setupEventListeners(): void {
    this.canvas.addEventListener('mousedown', (e) => this.handlePointerDown(e.clientX, e.clientY));
    // Listen for mousemove on window to track dragging outside canvas
    window.addEventListener('mousemove', (e) => this.handlePointerMove(e.clientX, e.clientY));
    // Listen for mouseup on window to catch releases outside canvas
    window.addEventListener('mouseup', (e) => this.handlePointerUp(e.clientX, e.clientY));

    this.canvas.addEventListener('touchstart', (e) => {
      e.preventDefault();
      const touch = e.touches[0];
      this.handlePointerDown(touch.clientX, touch.clientY);
    });

    // Listen for touchmove on window to track dragging outside canvas
    window.addEventListener('touchmove', (e) => {
      e.preventDefault();
      if (e.touches.length > 0) {
        const touch = e.touches[0];
        this.handlePointerMove(touch.clientX, touch.clientY);
      }
    });

    // Listen for touchend on window to catch releases outside canvas
    window.addEventListener('touchend', (e) => {
      e.preventDefault();
      if (e.changedTouches.length > 0) {
        const touch = e.changedTouches[0];
        this.handlePointerUp(touch.clientX, touch.clientY);
      }
    });
  }

  private handlePointerDown(clientX: number, clientY: number): void {
    if (!this.board) return;

    const rect = this.canvas.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    const piece = this.board.getPieceAt(x, y);
    if (piece) {
      this.selectedPiece = piece;
      this.selectedGroup = this.board.getConnectedGroup(piece);

      this.dragStartX = x;
      this.dragStartY = y;
      this.currentMouseX = x;
      this.currentMouseY = y;
      this.originalGridRow = piece.gridRow;
      this.originalGridCol = piece.gridCol;
      this.isDragging = true;

      // Calculate offsets for all pieces in the group
      this.groupOffsets.clear();
      for (const groupPiece of this.selectedGroup) {
        this.groupOffsets.set(groupPiece, {
          x: x - groupPiece.currentX,
          y: y - groupPiece.currentY
        });
        groupPiece.isSelected = true;
      }

      this.render();
    }
  }

  private handlePointerMove(clientX: number, clientY: number): void {
    if (!this.selectedPiece || !this.board) return;

    const rect = this.canvas.getBoundingClientRect();
    let x = clientX - rect.left;
    let y = clientY - rect.top;

    // Find the bounds of the dragged group
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const piece of this.selectedGroup) {
      const offset = this.groupOffsets.get(piece);
      if (offset) {
        const dragX = x - offset.x;
        const dragY = y - offset.y;
        minX = Math.min(minX, dragX);
        minY = Math.min(minY, dragY);
        maxX = Math.max(maxX, dragX + piece.width);
        maxY = Math.max(maxY, dragY + piece.height);
      }
    }

    // Get board boundaries
    const board = this.board as any;
    const boardLeft = board.offsetX;
    const boardTop = board.offsetY;
    const boardRight = board.offsetX + board.boardWidth;
    const boardBottom = board.offsetY + board.boardHeight;

    // Constrain the mouse position so the group stays within bounds
    if (minX < boardLeft) {
      x += (boardLeft - minX);
    }
    if (minY < boardTop) {
      y += (boardTop - minY);
    }
    if (maxX > boardRight) {
      x -= (maxX - boardRight);
    }
    if (maxY > boardBottom) {
      y -= (maxY - boardBottom);
    }

    // Update current mouse position for dragging visual
    this.currentMouseX = x;
    this.currentMouseY = y;

    this.render();
  }

  private handlePointerUp(clientX: number, clientY: number): void {
    if (!this.selectedPiece || !this.board) return;

    const rect = this.canvas.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    // Calculate the drag distance
    const dragDeltaX = x - this.dragStartX;
    const dragDeltaY = y - this.dragStartY;

    // Get board info
    const board = this.board as any;
    const pieceWidth = board.pieceWidth;
    const pieceHeight = board.pieceHeight;

    // Calculate how many grid positions to move (rounded to nearest grid position)
    const gridDeltaCol = Math.round(dragDeltaX / pieceWidth);
    const gridDeltaRow = Math.round(dragDeltaY / pieceHeight);

    // If no movement, just deselect
    if (gridDeltaCol === 0 && gridDeltaRow === 0) {
      for (const groupPiece of this.selectedGroup) {
        groupPiece.isSelected = false;
      }
      this.isDragging = false;
      this.selectedPiece = null;
      this.selectedGroup.clear();
      this.groupOffsets.clear();
      this.render();
      return;
    }

    // Calculate the target grid position for the selected piece
    let targetRow = this.originalGridRow + gridDeltaRow;
    let targetCol = this.originalGridCol + gridDeltaCol;

    // Constrain to grid boundaries for the entire group
    // Find the bounds of the group relative to the selected piece
    let minRowOffset = 0, maxRowOffset = 0, minColOffset = 0, maxColOffset = 0;
    for (const piece of this.selectedGroup) {
      const rowOffset = piece.gridRow - this.originalGridRow;
      const colOffset = piece.gridCol - this.originalGridCol;
      minRowOffset = Math.min(minRowOffset, rowOffset);
      maxRowOffset = Math.max(maxRowOffset, rowOffset);
      minColOffset = Math.min(minColOffset, colOffset);
      maxColOffset = Math.max(maxColOffset, colOffset);
    }

    // Constrain target position so the entire group stays within grid
    targetRow = Math.max(-minRowOffset, Math.min(targetRow, this.rows - 1 - maxRowOffset));
    targetCol = Math.max(-minColOffset, Math.min(targetCol, this.cols - 1 - maxColOffset));

    // Calculate the actual offset for the move
    const rowOffset = targetRow - this.originalGridRow;
    const colOffset = targetCol - this.originalGridCol;

    // If there's no movement, we're done
    if (rowOffset === 0 && colOffset === 0) {
      // Deselect all pieces in the group
      for (const groupPiece of this.selectedGroup) {
        groupPiece.isSelected = false;
      }
      this.isDragging = false;
      this.selectedPiece = null;
      this.selectedGroup.clear();
      this.groupOffsets.clear();
      this.render();
      return;
    }

    // Perform the move using the Board's internal method
    this.board.moveGroup(this.selectedGroup, rowOffset, colOffset);

    if (this.board.isSolved()) {
      this.onPuzzleComplete();
    }

    // Deselect all pieces in the group
    for (const groupPiece of this.selectedGroup) {
      groupPiece.isSelected = false;
    }

    this.isDragging = false;
    this.selectedPiece = null;
    this.selectedGroup.clear();
    this.groupOffsets.clear();

    this.render();
  }

  private onPuzzleComplete(): void {
    console.log('Puzzle completed!');
    this.isAnimatingWin = true;
    this.animationStartTime = Date.now();
    this.createConfetti();
    this.animateWin();

    // Call completion callback if set
    if (this.onCompleteCallback) {
      this.onCompleteCallback();
    }
  }

  private createConfetti(): void {
    const colors = ['#ff6b6b', '#4ecdc4', '#45b7d1', '#f9ca24', '#f0932b', '#eb4d4b', '#6c5ce7', '#a29bfe'];
    this.confetti = [];

    for (let i = 0; i < 150; i++) {
      this.confetti.push({
        x: Math.random() * this.canvas.width,
        y: -20 - Math.random() * this.canvas.height,
        vx: (Math.random() - 0.5) * 4,
        vy: Math.random() * 3 + 2,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: Math.random() * 8 + 4,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.2,
      });
    }
  }

  private animateWin(): void {
    if (!this.isAnimatingWin) return;

    const elapsed = Date.now() - this.animationStartTime;

    // Update confetti
    for (const particle of this.confetti) {
      particle.x += particle.vx;
      particle.y += particle.vy;
      particle.rotation += particle.rotationSpeed;
      particle.vy += 0.1; // gravity

      // Wrap around horizontally
      if (particle.x < 0) particle.x = this.canvas.width;
      if (particle.x > this.canvas.width) particle.x = 0;
    }

    this.render();

    // Stop after 5 seconds
    if (elapsed < 5000) {
      requestAnimationFrame(() => this.animateWin());
    } else {
      this.isAnimatingWin = false;
      this.confetti = [];
      this.render();
    }
  }

  private render(): void {
    if (!this.board || !this.image) return;

    const container = this.canvas.parentElement;
    const displayWidth = container ? container.clientWidth : this.canvas.width;
    const displayHeight = container ? container.clientHeight : this.canvas.height;

    this.ctx.fillStyle = '#2a2a2a';
    this.ctx.fillRect(0, 0, displayWidth, displayHeight);

    // Calculate scale animation for win celebration
    let scale = 1;
    if (this.isAnimatingWin) {
      const elapsed = Date.now() - this.animationStartTime;
      // Bulge animation: scale up to 1.08 in first 300ms, then back to 1.0 by 600ms
      if (elapsed < 300) {
        scale = 1 + (elapsed / 300) * 0.08;
      } else if (elapsed < 600) {
        scale = 1.08 - ((elapsed - 300) / 300) * 0.08;
      } else {
        scale = 1;
      }
    }

    // Apply scale transformation if animating
    if (scale !== 1 && this.board) {
      const board = this.board as any;
      const centerX = board.offsetX + board.boardWidth / 2;
      const centerY = board.offsetY + board.boardHeight / 2;

      this.ctx.save();
      this.ctx.translate(centerX, centerY);
      this.ctx.scale(scale, scale);
      this.ctx.translate(-centerX, -centerY);
    }

    this.board.draw(this.ctx, this.image);

    if (scale !== 1) {
      this.ctx.restore();
    }

    // Draw dragging pieces on top if currently dragging
    if (this.isDragging && this.selectedGroup.size > 0) {
      const sourceWidth = this.image.width / this.cols;
      const sourceHeight = this.image.height / this.rows;

      for (const piece of this.selectedGroup) {
        const offset = this.groupOffsets.get(piece);
        if (offset) {
          // Calculate the position of this piece relative to the mouse
          const dragX = this.currentMouseX - offset.x;
          const dragY = this.currentMouseY - offset.y;

          // Draw the piece at the dragged position
          const sourceX = piece.col * sourceWidth;
          const sourceY = piece.row * sourceHeight;

          this.ctx.drawImage(
            this.image,
            sourceX,
            sourceY,
            sourceWidth,
            sourceHeight,
            dragX,
            dragY,
            piece.width,
            piece.height
          );
        }
      }
    }

    // Update puzzle info (credit) in DOM
    const puzzleInfoElement = document.getElementById('puzzle-info');
    if (puzzleInfoElement) {
      const credit = this.getPhotoCredit();
      puzzleInfoElement.textContent = credit || '';
    }

    // Draw confetti on top of everything
    if (this.confetti.length > 0) {
      for (const particle of this.confetti) {
        this.ctx.save();
        this.ctx.translate(particle.x, particle.y);
        this.ctx.rotate(particle.rotation);
        this.ctx.fillStyle = particle.color;
        this.ctx.fillRect(-particle.size / 2, -particle.size / 2, particle.size, particle.size);
        this.ctx.restore();
      }
    }
  }
}
